import React from 'react';
import Styles from './index.less';
import { Feature, MainMenu } from '@tencent/workbench';
import { config, TransitionList, menuConfig } from './config';
import BaseSelectablePanelFeature from '@framework/workbench/common/feature/base-selectable-panel-feature';
import SelectablePanel from '@framework/workbench/common/components/selectable-panel';
import i18n from '@i18next/i18n';
import { IReactionDisposer } from 'mobx/lib/internal';
import TransitionCard from '@framework/workbench/common/components/selectable-panel/transition-card';
import { TransitionType } from '@src/slide/domain/ui-model/element-modeles/transition/constants';
import { reaction } from 'mobx';
import { transitionEffectMapping } from '@framework/workbench/ui/transition/transition-effect-option/effect-mapping';
import { ITransitionEffect } from '@framework/workbench/ui/transition/transition-effect-option/transition-effect-list';
import get from 'lodash/get';

@Feature()
export class TransitionChangeSimpleFeature extends BaseSelectablePanelFeature {
    public id = config.id;
    public config = config;
    public initReaction: IReactionDisposer;
    private previewTimer: NodeJS.Timeout;

    public constructor() {
        super();

        this.initReaction = reaction(() => this.currentSlide, () => {
            this.toolbarService.$forceUpdate();
        });
    }

    public handleTransitionChange = (type: TransitionType) => {
        this.transitionService?.promise().then((module) => {
            module.updateTransitionType(type);

            if (module.autoPreviewState) {
                clearTimeout(this.previewTimer);

                // 更新model之后会触发 slide-wrapper的update，里面会reset transition-layer
                // 这里延迟预览，保证以新的layer context进行preview
                this.previewTimer = setTimeout(() => {
                    module.preview();
                }, 100);
            }
        });
    };

    public handleApplyAll() {
        this.transitionService?.promise().then((module) => {
            module.applyTransitionToAll();
        });
    }

    public getDropDownContent() {
        if (this.currentSlideTransitionType === TransitionType.EMPTY) {
            // 没有切页动画的时候显示选择面板
            return this.getSelectPanelContent();
        }

        return this.getEffectPanelContent();
    }

    public getTransitionEffectDesc() {
        return this.getEffectOption()?.title;
    }

    public getTransitionDuration() {
        return `${(this.currentSlide?.transition?.property.duration || 0) / 1000}s`;
    }

    public get currentSlideTransitionType() {
        const type = this.currentSlideTransition?.type;

        // newsflash 是 warp 菜单下的一种效果，但是独立除了一种type
        if (type === TransitionType.NEWSFLASH) {
            return TransitionType.WARP;
        }

        return type || TransitionType.EMPTY;
    }

    private getEffectPanelContent() {
        return <MainMenu align="left" name={menuConfig.id} mode="menu" itemStyle={{ width: 200 }} />;
    }

    private getSelectPanelContent() {
        return  <div className={Styles['transition-change']}>
            <SelectablePanel
                wrapperStyle = {{
                    width: '555px',
                }}
                backgroundStyle={{
                    background: 'rgba(255, 255, 255, 1)',
                }}
                expandHeight={123}
                colNumber={10}
                collapsed={false}
                showArrow={false}
                selectedId={this.currentSlideTransitionType}
                childrenElements={
                    [
                        {
                            name: i18n.t('细微'),
                            cardList: TransitionList.map(item => ({
                                element: <TransitionCard
                                    key={item.type}
                                    icon={item.icon}
                                    type={item.type}
                                    text={item.text}
                                    tooltip={item?.tooltip}
                                    onClick={this.handleTransitionChange}
                                    id={item.type}
                                />,
                                id: item.type,
                            })),
                        },
                    ]
                } />
        </div>;
    }

    private get transitionService() {
        return this.getAppService().editableServiceLayer?.transitionService;
    }

    private get currentSlide() {
        return this.getAppService().viewableServiceLayer.selectionService.currentSlide;
    }

    private get currentSlideTransition() {
        return this.currentSlide?.transition;
    }

    private getEffectOption() {
        const mapping = transitionEffectMapping[this.currentSlideTransitionType];
        if (!mapping) return;

        // 无效果选项的
        if (mapping?.disable) {
            return mapping as ITransitionEffect;
        }

        // 特殊类型的，有选项但属于其他type的一个options的，如newsflash
        if (mapping?.data) {
            return mapping.data;
        }

        const value = get(this.currentSlideTransition, mapping.conditionKey!);

        if (mapping.bool) {
            const key = !!value;
            return mapping.conditions?.[key as any];
        }

        return mapping?.conditions?.[value];
    }
}
